import * as yup from "yup";
import dayjs from "dayjs";
import { ProductDataRemovalTaskFormValues } from "./product-data-removal-task-form";

type UseProductDataRemovalTaskCreateFormParams = {
  tenantSlug: string;
  disableDateRangeValidation?: boolean;
};

export const useProductDataRemovalTaskCreateForm = ({
  tenantSlug,
  disableDateRangeValidation = false,
}: UseProductDataRemovalTaskCreateFormParams) => {
  const initialValues: ProductDataRemovalTaskFormValues = {
    dueAt: "",
    slug: "",
  };
  const validationSchema = yup.object().shape({
    dueAt: yup
      .string()
      .required(REQUIRED_ERROR)
      .test("date-format", DATE_FORMAT_ERROR, validateDateRegex)
      .test("date-validity", DATE_VALIDITY_ERROR, validateDateValidity)
      .test(
        "date-range",
        DATE_RANGE_ERROR,
        validateDateRange(new Date(), disableDateRangeValidation),
      ),
    slug: yup
      .string()
      .required(REQUIRED_ERROR)
      .test("slug-match", SLUG_MISMATCH_ERROR, validateSlugMatch(tenantSlug)),
  });

  return {
    initialValues,
    validationSchema,
    dateConfig: {
      DateFormat: DATE_FORMAT,
      MinDate: getMinDate(new Date()).format(DATE_FORMAT),
      MaxDate: getMaxDate(new Date()).format(DATE_FORMAT),
    },
  };
};

const DATE_FORMAT = "YYYY-MM-DD";
const REQUIRED_ERROR = "必須項目です。";
const SLUG_MISMATCH_ERROR = "テナントのスラグが一致しません。";
const DATE_RANGE_ERROR = "翌日から30日後までの間で指定してください。";
const DATE_VALIDITY_ERROR = "有効な日付ではありません。";
const DATE_FORMAT_ERROR = `${DATE_FORMAT}形式で入力してください。（例: 2025-01-15）`;

const getMinDate = (now: Date) => dayjs(now).add(1, "day").startOf("day");
const getMaxDate = (now: Date) => dayjs(now).add(30, "day").endOf("day");

export const validateDateRegex = (value: string) => {
  const dateFormatRegex = /^\d{4}-\d{2}-\d{2}$/;
  return dateFormatRegex.test(value);
};

export const validateDateValidity = (value: string) => {
  const selectedDate = dayjs(value, DATE_FORMAT, true);
  if (!selectedDate.isValid()) {
    return false;
  }

  // 元の文字列と解析後の日付が一致するかチェック
  // Day.jsが自動調整した場合、フォーマット後の文字列が元の値と異なる
  // これにより8月32日などが無効な日付として検知される
  const formattedDate = selectedDate.format(DATE_FORMAT);
  return formattedDate === value;
};

export const validateDateRange =
  (now: Date, disabled: boolean) => (value: string) => {
    if (disabled) return true;
    const selectedDate = dayjs(value, DATE_FORMAT, true);
    return (
      selectedDate.valueOf() >= getMinDate(now).valueOf() &&
      selectedDate.valueOf() <= getMaxDate(now).valueOf()
    );
  };

export const validateSlugMatch = (tenantSlug: string) => (value: string) => {
  return value === tenantSlug;
};
